import { AGENT_TILE_WIDTH, AgentTile } from '@/components/AgentTile';
import { LibrarySkeleton } from '@/components/Library/LibrarySkeleton';
import { HttpStatus } from '@/errors/http-status';
import { AgentShareModal, DeleteAgentModal, DeleteEmbedAgentModal, EmbedAgentModal } from '@/pages/agents/components/modals';
import { SectionTitle } from '@/pages/home/components/Common';
import { useAuth } from '@/services/auth';
import { useClientKey } from '@/state/agent';
import { defaultErrorHandler } from '@/utils/default-error-handler';
import { AppRoutes } from '@/utils/types';
import { IAgent } from '@d-id/types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVideoDisplayLimit } from '../../hooks/useVideoDisplayLimit';
import {
    WorkspaceSectionContainer,
    WorkspaceSectionHeader,
    WorkspaceSectionHeaderButton,
    WorkspaceTileList,
} from '../workspaceComponents/common';

interface Props {
    className?: string;
    agents: IAgent[];
    isLoading: boolean;
    onEditAgent: (agent: IAgent) => void;
}

export const MyAgents = ({ className, agents, isLoading, onEditAgent }: Props) => {
    const navigate = useNavigate();
    const { isAnonymous } = useAuth();
    const ref = useRef<HTMLDivElement | null>(null);

    const [itemToDelete, setItemToDelete] = useState<IAgent | null>(null);
    const [itemToShare, setItemToShare] = useState<IAgent | undefined>(undefined);
    const [itemToRemoveEmbed, setItemToRemoveEmbed] = useState<IAgent | null>(null);
    const [embedModalState, setEmbedModalState] = useState(false);
    const { data: clientKey } = useClientKey({ isAnonymous });

    const limit = useVideoDisplayLimit(ref, agents.length, AGENT_TILE_WIDTH);
    const elements = [...agents].splice(0, limit);
    const showSeeAll = agents.length > elements.length;

    if (isLoading && !isAnonymous) {
        return (
            <WorkspaceSectionContainer>
                <WorkspaceSectionHeader>
                    <SectionTitle id="my-agents-section">My Agents</SectionTitle>
                </WorkspaceSectionHeader>
                <LibrarySkeleton amount={4} tileHeight="350px" tileWidth={`${AGENT_TILE_WIDTH}px`} maxRows={1} />;
            </WorkspaceSectionContainer>
        );
    }

    if (!agents?.length) {
        return null;
    }

    return (
        <WorkspaceSectionContainer className={className}>
            <WorkspaceSectionHeader>
                <SectionTitle id="my-agents-section">My Agents</SectionTitle>
                {showSeeAll && (
                    <WorkspaceSectionHeaderButton onClick={() => navigate(AppRoutes.Agent)}>See all</WorkspaceSectionHeaderButton>
                )}
            </WorkspaceSectionHeader>

            <WorkspaceTileList tileWidthPx={AGENT_TILE_WIDTH} ref={ref}>
                {elements.map(agent => (
                    <AgentTile
                        key={agent.id}
                        agent={agent}
                        onDelete={(agent: IAgent) => setItemToDelete(agent)}
                        onEmbed={(agent: IAgent) => setItemToShare(agent)}
                        onRemoveEmbed={(agent: IAgent) => setItemToRemoveEmbed(agent)}
                        onEdit={onEditAgent}
                        clientKey={clientKey?.client_key}
                    />
                ))}
            </WorkspaceTileList>
            <DeleteEmbedAgentModal itemToDelete={itemToRemoveEmbed} onClose={() => setItemToRemoveEmbed(null)} />
            <DeleteAgentModal itemToDelete={itemToDelete} onClose={() => setItemToDelete(null)} />
            <AgentShareModal
                agent={itemToShare}
                open={!!itemToShare}
                onClose={() => setItemToShare(undefined)}
                errorHandler={async error => {
                    if (error?.response?.status === HttpStatus.MAX_EMBED_AGENTS_LIMIT_REACHED) {
                        console.warn('Max embed agents limit reached');
                        setEmbedModalState(true);
                        setItemToShare(undefined);
                    } else {
                        defaultErrorHandler(error);
                    }
                }}
            />
            <EmbedAgentModal isOpen={embedModalState} onClose={() => setEmbedModalState(false)} />
        </WorkspaceSectionContainer>
    );
};
